import { useState, useCallback, ReactNode } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { NavigationDots, NavigationArrows } from "~/ui-rtk/components/atoms"

type ScrollableSectionProps = {
  title: string
  items: Array<{ id: string; title: string; content: ReactNode }>
  className?: string
}

export default function ScrollableSection({
  title,
  items,
  className,
}: ScrollableSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0)

  const handlePrevious = useCallback(() => {
    setActiveIndex(prev => (prev === 0 ? items.length - 1 : prev - 1))
  }, [items.length])

  const handleNext = useCallback(() => {
    setActiveIndex(prev => (prev === items.length - 1 ? 0 : prev + 1))
  }, [items.length])

  const currentItem = items[activeIndex]
  const canNavigate = items.length > 1

  return (
    <div
      className={cn(
        "relative rounded-2xl h-full overflow-hidden",
        "bg-gradient-to-br from-system-blue-800/60 to-system-blue-900/80",
        "border border-system-blue-600/40",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-system-blue-600/30">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {currentItem && (
            <span className="text-sm text-system-gray-400 px-3 py-1 rounded-full bg-system-blue-700/50">
              {currentItem.title}
            </span>
          )}
        </div>

        {/* Navigation Controls */}
        {canNavigate && (
          <div className="flex items-center gap-3">
            <NavigationDots
              total={items.length}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
              size="md"
              className="mr-2"
            />
            <NavigationArrows
              onPrevious={handlePrevious}
              onNext={handleNext}
              size="lg"
            />
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="relative px-6  overflow-hidden">
        <div key={currentItem?.id} className="animate-fadeIn">
          {currentItem?.content}
        </div>
      </div>
    </div>
  )
}
